/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { RiArrowLeftSLine } from 'react-icons/ri';
import { format } from 'date-fns';

import { RebrandShell } from 'components/rebrand/RebrandShell';
import { UserAvatar } from 'components/user-avatar';
import { useAuth } from 'context/AuthContext';
import { dimensaoByBehavior, DIMENSOES, nivelByMoeda } from 'components/rebrand/mappings';
import { getKPIPublicHistoryByTime, getPlayerInfo } from 'services/dashboard.service';
import { getPublicProfile } from 'services/profile.service';
import { convertEmojis } from 'utils/helpers';

import './styles.scss';

function PersonMini({ name, unit, avatar }: { name: string; unit?: string; avatar?: any }) {
  return (
    <div className="rb-personmini">
      <div className="rb-personmini__avatar">
        <UserAvatar data={{ ...(avatar || {}), ariaHidden: true }} responsive={true} />
      </div>
      <div>
        <div className="rb-personmini__name">{name}</div>
        {!!unit && <div className="rb-personmini__unit">{unit}</div>}
      </div>
    </div>
  );
}

function RecognitionCard({ item, receiver }: { item: any; receiver: { name: string; unit?: string; avatar?: any } }) {
  const dimensao = dimensaoByBehavior(item.behavior);
  // O nível (#RECONHECIMENTO/#DESTAQUE/#REFERÊNCIA) depende da moeda (`coin`).
  // O aggregate público ainda não retorna `coin`, então só exibimos a tag quando
  // ela realmente existir — evita mostrar um nível fixo/incorreto.
  const nivel = item.coin ? nivelByMoeda(item.coin) : undefined;
  return (
    <article className="rb-profcard">
      <PersonMini
        name={item.sender_nome_guerra || item.sender_name}
        unit={item.sender_desc_unidade || item.sender_funcao}
        avatar={item.sender_avatar}
      />
      <div className="rb-profcard__flow">
        <span className="rb-profcard__flow-label">Enviou</span>
        <span className="rb-dimchip" style={{ color: dimensao.color, boxShadow: `inset 0 0 0 1.5px ${dimensao.color}` }}>
          <span className="rb-dimchip__dot" style={{ background: dimensao.color }} />
          {dimensao.label}
        </span>
        {!!nivel && <span className="rb-profcard__nivel">{nivel.tag}</span>}
        <span className="rb-profcard__flow-label">para</span>
      </div>
      <PersonMini name={receiver.name} unit={receiver.unit} avatar={receiver.avatar} />
      {!!item.message && (
        <p className="rb-profcard__msg" dangerouslySetInnerHTML={{ __html: '“' + convertEmojis(item.message) + '”' }} />
      )}
      <div className="rb-profcard__date">{format(new Date(item.time), 'dd/MM - HH:mm')}</div>
    </article>
  );
}

export default function Perfil() {
  const { id } = useParams();
  const { currentCycle } = useAuth();

  const [profile, setProfile] = useState<any>();
  const [info, setInfo] = useState<any>();
  const [received, setReceived] = useState<any[]>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      if (!id || !currentCycle) return;
      setLoading(true);
      try {
        const [prof, playerInfo, history] = await Promise.all([
          getPublicProfile(id),
          getPlayerInfo(id),
          getKPIPublicHistoryByTime(id, currentCycle.date_start, currentCycle.date_finish),
        ]);
        setProfile(prof);
        setInfo(playerInfo);
        setReceived((Array.isArray(history) ? history : []).filter((x: any) => !x.cancelled));
      } catch (e) {
        setReceived([]);
      }
      setLoading(false);
    }
    loadData();
  }, [id, currentCycle]);

  const name = info?.nome_guerra || info?.nome || profile?.name || '';
  const funcao = info?.funcao || info?.nm_funcao;
  const unit = info?.desc_unidade || info?.nome_dependencia || info?.diretoria;
  const avatar = { ...(profile || {}), ...(profile?.extra || {}) };

  const porDimensao = DIMENSOES.map(d => ({
    ...d,
    count: (received || []).filter((r: any) => r.behavior === d.id).length,
  })).filter(d => d.count > 0);

  return (
    <RebrandShell title="Perfil do reconhecido">
      <Link to="/galeria" className="rb-perfil__back">
        <RiArrowLeftSLine size={18} /> Voltar à galeria
      </Link>

      {loading ? (
        <div className="rb-skeleton" style={{ height: 300 }} />
      ) : (
        <>
          <div className="rb-perfil__head">
            <div className="rb-perfil__avatar">
              <UserAvatar data={{ ...avatar, ariaHidden: true }} responsive={true} />
            </div>
            <div>
              <h1 className="rb-perfil__name">{name}</h1>
              <div className="rb-perfil__role">
                {funcao}
                {funcao && unit ? ' · ' : ''}
                {unit}
              </div>
              {!!info?.prefixo && <div className="rb-perfil__meta">Unidade nº {info.prefixo}</div>}
            </div>
          </div>

          <div className="rb-perfil__stats">
            <div className="rb-perfil__stat">
              <div className="rb-perfil__stat-value">{received?.length || 0}</div>
              <div className="rb-perfil__stat-label">Reconhecimentos recebidos</div>
            </div>
            {porDimensao.map(d => (
              <div key={d.id} className="rb-perfil__stat">
                <div className="rb-perfil__stat-value" style={{ color: d.color }}>
                  {d.count}
                </div>
                <div className="rb-perfil__stat-label">{d.label}</div>
              </div>
            ))}
          </div>

          <h2 className="rb-perfil__section">Reconhecimentos recebidos</h2>

          {received && received.length > 0 ? (
            <div className="rb-perfil__grid">
              {received.map((item: any) => (
                <RecognitionCard key={item._id} item={item} receiver={{ name, unit, avatar }} />
              ))}
            </div>
          ) : (
            <p className="rb-perfil__empty">Ainda não recebeu reconhecimentos neste ciclo.</p>
          )}
        </>
      )}
    </RebrandShell>
  );
}
